/**
 * i18n infrastructure. English is the default; German is second and uses SWISS
 * conventions — "ss", never "ß".
 *
 * A flat key → string map per locale. Keys are dotted namespaces. Missing keys fall
 * back to English, then to the key itself.
 */

export type Locale = 'en' | 'de';

export const LOCALES: { code: Locale; label: string }[] = [
  { code: 'en', label: 'English' },
  { code: 'de', label: 'Deutsch' },
];

type Dict = Record<string, string>;

const en: Dict = {
  'app.name': 'SoulGraph',
  'app.tagline': 'Human Intelligence Platform',

  'nav.graph': 'My SoulGraph',
  'nav.privacy': 'Privacy',
  'nav.method': 'Method',
  'nav.settings': 'Settings',
  'nav.unlock': 'Unlock',
  'nav.about': 'About',
  'nav.begin': 'Create my SoulGraph',

  'landing.headline': 'Know yourself. Understand others.',
  'landing.subline': 'One private map of how you think, decide, relate and act.',
  'landing.cta': 'Create my SoulGraph',
  'landing.cta_note': 'No account required',
  'landing.law': 'One SoulGraph. Many applications. User controlled. AI augmented. Human connected.',

  'landing.how.title': 'How it works',
  'landing.how.assess.title': 'Assess',
  'landing.how.assess.body': 'Answer validated instruments at your own pace. No right answers — only yours.',
  'landing.how.map.title': 'Map',
  'landing.how.map.body': 'Your answers are scored on your device into a structured, versioned map of you.',
  'landing.how.understand.title': 'Understand',
  'landing.how.understand.body': 'Turn the map into clear insight about how you think, decide, relate and act.',

  'landing.privacy.title': 'Your SoulGraph belongs to you',
  'landing.privacy.body': 'Your answers are scored on your device, encrypted before anything leaves it, and never published. Relay sync is off by default. There is no server that can read your SoulGraph — because there is no server.',

  'landing.evidence.note': 'Every insight carries an evidence label — Psychometric, Interpretive, or Experimental SoulGraph. SoulGraph never diagnoses, never judges, and is not psychotherapy.',

  'evidence.psychometric': 'Psychometric',
  'evidence.interpretive': 'Interpretive',
  'evidence.experimental': 'Experimental SoulGraph',

  'method.evidence.title': 'Three evidence labels',
  'method.evidence.psychometric.body': 'Built on constructs with substantial empirical support — for example the Big Five (OCEAN).',
  'method.evidence.interpretive.body': 'Drawn from established interpretive traditions — for example Jungian typology or the Enneagram.',
  'method.evidence.experimental.body': 'Proprietary or speculative composites — Life Pattern, Human Design, and SoulGraph Core.',
  'method.boundaries.title': 'What SoulGraph will never do',
  'method.boundaries.body': 'SoulGraph never diagnoses mental illness, psychiatric or medical conditions, criminality or dangerousness. It never produces hire, fire, untrustworthy or unstable verdicts, and never presents probabilistic interpretation as certainty. SoulGraph is not psychotherapy. Organizations never own employee SoulGraphs — this is enforced cryptographically, not by terms of service.',

  'page.begin.title': 'Create your SoulGraph',
  'page.begin.body': 'A short, 15-question demo to get started. Your answers carry over into your full SoulGraph — nothing is re-asked.',
  'page.graph.title': 'My SoulGraph',
  'page.graph.body': 'Your private, versioned map. Facets appear here as you complete assessments.',
  'page.onepager.title': 'SoulGraph one-pager',
  'page.onepager.body': 'A single-page summary of your SoulGraph, for yourself or to share by consent.',
  'page.privacy_center.title': 'Privacy Center',
  'page.privacy_center.body': 'Control sync, grants and revocation. Nothing psychological ever leaves your device unencrypted.',
  'page.settings.title': 'Settings',
  'page.settings.body': 'Identity, theme, language and sync relays.',
  'page.method.title': 'Method & evidence',
  'page.method.body': 'How SoulGraph scores, what each evidence label means, and what SoulGraph will never do.',
  'page.legal.privacy.title': 'Privacy',
  'page.legal.terms.title': 'Terms',

  'placeholder.coming': 'Charted — not yet built',

  /* --- Assessment --- */
  'assessment.progress.item': 'Item',
  'assessment.likert.aria': 'How much do you agree with this statement?',
  'assessment.likert.disagree': 'Disagree',
  'assessment.likert.agree': 'Agree',
  'assessment.nav.back': 'Back',
  'assessment.nav.next': 'Next',
  'assessment.nav.finish': 'See my SoulGraph',
  'assessment.nav.scoring': 'Scoring on your device…',
  'assessment.footer.local': 'Scored on your device. Encrypted before anything is stored. Never published.',
  'assessment.footer.no_encrypt': 'Your current signer cannot encrypt. Use a NIP-07 extension or a local key that supports NIP-44 to save your SoulGraph.',
  'assessment.done.title': 'Your SoulGraph is mapped',
  'assessment.done.body': 'Your answers were scored on your device and sealed into an encrypted, versioned map. Explore it now.',
  'assessment.done.cta': 'Open my SoulGraph',

  /* --- Graph --- */
  'graph.summary.title': 'Overall confidence',
  'graph.summary.scaled': 'Scaled, not population-normed — a value shows how far up the possible range you scored, never a percentile.',
  'graph.summary.full_cta': 'Go deeper',
  'graph.footer.boundaries': 'SoulGraph never diagnoses, never judges, and never presents interpretation as certainty. It is not psychotherapy. This is a reflective map — a tendency, not a box.',
  'graph.signin.body': 'Your SoulGraph is private to you. Create or sign in to a Nostr identity to begin.',
  'graph.empty.body': 'You have not mapped a SoulGraph yet. The free 15-question demo takes a couple of minutes.',
  'graph.empty.cta': 'Take the demo',
  'graph.jung.code_note': 'a convenience label, not a box',
  'graph.life.code_note': 'reflective pattern number',
  'graph.enneagram.unclear': 'Your top two patterns are too close to call yet. Answer the full assessment to clarify — we will not pick one for you.',
  'graph.enneagram.wing': 'leaning',
  'graph.core.title': 'SoulGraph Core',
  'graph.core.note': 'Experimental composites across your other facets. A composite is only as grounded as its inputs, and the combination itself is proprietary — always experimental.',
  'graph.identity.title': 'Identity',
  'graph.identity.note': 'Only your public key. Never your legal name, phone or address.',

  /* Jung axis poles (low ←→ high) */
  'jung.pole.energy.low': 'Inward',
  'jung.pole.energy.high': 'Outward',
  'jung.pole.perception.low': 'Patterns',
  'jung.pole.perception.high': 'Detail',
  'jung.pole.judgment.low': 'Values',
  'jung.pole.judgment.high': 'Logic',
  'jung.pole.lifestyle.low': 'Open',
  'jung.pole.lifestyle.high': 'Planned',

  /* SoulGraph Core dimensions */
  'core.dim.autonomy': 'Autonomy',
  'core.dim.analytical_orientation': 'Analytical orientation',
  'core.dim.emotional_sensitivity': 'Emotional sensitivity',
  'core.dim.social_energy': 'Social energy',
  'core.dim.exploration': 'Exploration',
  'core.dim.structure': 'Structure',
  'core.dim.risk_tolerance': 'Risk tolerance',
  'core.dim.harmony': 'Harmony',
  'core.dim.assertiveness': 'Assertiveness',
  'core.dim.meaning_orientation': 'Meaning orientation',
  'core.dim.stability': 'Stability',
  'core.dim.adaptability': 'Adaptability',
  'core.dim.novelty_seeking': 'Novelty seeking',
  'core.dim.cooperation': 'Cooperation',
  'core.dim.dominance': 'Dominance',
  'core.dim.introspection': 'Introspection',

  /* --- Complete assessment (full) --- */
  'page.assess.kicker': 'Complete SoulGraph',
  'page.assess.title': 'Complete your SoulGraph',
  'page.assess.body': 'The full assessment across every instrument. You continue exactly where the demo left off — no question is ever repeated. Pause whenever you like; your progress is saved, encrypted, on your device.',
  'assess.signin.body': 'Create or sign in to a Nostr identity to take the full assessment.',
  'assess.locked.body': 'The Complete SoulGraph unlocks the full assessment and complete results.',
  'assess.locked.cta': 'Unlock Complete SoulGraph',
  'assessment.resumed': 'Resumed where you left off',
  'assessment.footer.resume': 'You can leave any time — your answers are saved as you go.',
  'assessment.allanswered.title': 'Everything is answered',
  'assessment.allanswered.body': 'You have already answered every item across all instruments. Score it into a new version of your SoulGraph.',

  'graph.action.onepager': 'One-pager',
  'graph.action.share': 'Share',

  /* --- Complete results --- */
  'results.title': 'Complete results',
  'results.method_link': 'How we read this →',
  'results.standing': 'Evidence',
  'results.tensions.title': 'Contradictions worth noticing',
  'results.tensions.intro': 'Where two frameworks pull in different directions. These are not problems to fix — they are questions to sit with. Only you can answer them.',
  'interpret.tentative': 'A slight lean:',

  /* framework explanations (results + method) */
  'interpret.jung.framework': 'A lens from Jungian tradition describing habitual preferences across four dimensions. It maps tendencies, not fixed categories.',
  'interpret.jung.standing': 'Interpretive. The dichotomies are widely used and intuitively useful, but type theory has limited predictive validity in peer-reviewed research.',
  'interpret.enneagram.framework': 'A map of nine recurring motivational patterns, each with a neighbouring "wing" that colours it.',
  'interpret.enneagram.standing': 'Interpretive. Rich as a reflective tradition; empirical support for the nine-type structure is modest and contested.',
  'interpret.life.framework': 'An experimental SoulGraph lens blending reflective life-theme questions with an optional numerological reading of your birth date.',
  'interpret.life.standing': 'Experimental. Numerology has no empirical validation; treat this purely as a prompt for reflection.',
  'interpret.core.framework': 'Proprietary SoulGraph composites that combine signals across your other facets into higher-level dimensions.',
  'interpret.core.standing': 'Experimental. A composite is only as grounded as its inputs, and the combination itself is unvalidated.',

  /* Jung per-axis interpretation */
  'interpret.jung.energy.high': 'You tend to draw energy from people and activity, thinking out loud and engaging outward.',
  'interpret.jung.energy.low': 'You tend to recharge in quiet and reflection, settling your thoughts inwardly before sharing.',
  'interpret.jung.energy.balanced': 'Your energy seems to move fluidly between outward engagement and inward reflection.',
  'interpret.jung.perception.high': 'You lean toward concrete detail and direct experience over abstraction.',
  'interpret.jung.perception.low': 'You lean toward patterns, possibilities and the bigger picture over literal detail.',
  'interpret.jung.perception.balanced': 'You seem to move comfortably between concrete detail and broader patterns.',
  'interpret.jung.judgment.high': 'You tend to weigh decisions by logic and consistency first.',
  'interpret.jung.judgment.low': 'You tend to weigh decisions by values and their human impact first.',
  'interpret.jung.judgment.balanced': 'You appear to balance logical consistency with human impact when deciding.',
  'interpret.jung.lifestyle.high': 'You tend to prefer things planned, settled and closed off.',
  'interpret.jung.lifestyle.low': 'You tend to prefer keeping options open and adapting as you go.',
  'interpret.jung.lifestyle.balanced': 'You seem to hold structure and openness in roughly equal measure.',

  /* Enneagram per-type interpretation */
  'interpret.enneagram.inconclusive': 'Your two strongest patterns are too close to separate confidently. Rather than name one, we leave this open — the full assessment may clarify it.',
  'interpret.enneagram.type.1': 'A pull toward getting things right and living by clear principles.',
  'interpret.enneagram.type.2': 'A pull toward sensing and meeting the needs of others.',
  'interpret.enneagram.type.3': 'A pull toward goals, progress and being effective.',
  'interpret.enneagram.type.4': 'A pull toward depth, authenticity and self-expression.',
  'interpret.enneagram.type.5': 'A pull toward understanding, privacy and conserving energy.',
  'interpret.enneagram.type.6': 'A pull toward foresight, loyalty and security.',
  'interpret.enneagram.type.7': 'A pull toward possibility, variety and keeping options open.',
  'interpret.enneagram.type.8': 'A pull toward taking charge and protecting what matters.',
  'interpret.enneagram.type.9': 'A pull toward calm, accord and inner steadiness.',
  'interpret.enneagram.wing.1.9': 'Your pattern is coloured by a quieter, more withdrawn tone.',
  'interpret.enneagram.wing.1.2': 'Your pattern is coloured by warmth and a wish to help.',
  'interpret.enneagram.wing.2.1': 'Your pattern is coloured by principle and self-discipline.',
  'interpret.enneagram.wing.2.3': 'Your pattern is coloured by ambition and a focus on results.',
  'interpret.enneagram.wing.3.2': 'Your pattern is coloured by warmth and attention to others.',
  'interpret.enneagram.wing.3.4': 'Your pattern is coloured by a search for depth and individuality.',
  'interpret.enneagram.wing.4.3': 'Your pattern is coloured by drive and a focus on achievement.',
  'interpret.enneagram.wing.4.5': 'Your pattern is coloured by reserve and a thinking, observing tone.',
  'interpret.enneagram.wing.5.4': 'Your pattern is coloured by imagination and emotional depth.',
  'interpret.enneagram.wing.5.6': 'Your pattern is coloured by caution and a wish for security.',
  'interpret.enneagram.wing.6.5': 'Your pattern is coloured by a reserved, analytical tone.',
  'interpret.enneagram.wing.6.7': 'Your pattern is coloured by optimism and a taste for variety.',
  'interpret.enneagram.wing.7.6': 'Your pattern is coloured by loyalty and an eye on risk.',
  'interpret.enneagram.wing.7.8': 'Your pattern is coloured by assertiveness and drive.',
  'interpret.enneagram.wing.8.7': 'Your pattern is coloured by enthusiasm and a taste for the new.',
  'interpret.enneagram.wing.8.9': 'Your pattern is coloured by steadiness and a calmer manner.',
  'interpret.enneagram.wing.9.8': 'Your pattern is coloured by quiet strength and resolve.',
  'interpret.enneagram.wing.9.1': 'Your pattern is coloured by principle and a sense of order.',

  /* Life Pattern themes (experimental) */
  'interpret.life.independence': 'A strong reflective pull toward self-direction and standing on your own.',
  'interpret.life.creativity': 'A strong reflective pull toward making, imagining and original expression.',
  'interpret.life.service': 'A strong reflective pull toward contributing to others and the common good.',
  'interpret.life.discipline': 'A strong reflective pull toward structure, follow-through and mastery.',
  'interpret.life.trust': 'A strong reflective pull toward openness and faith in others.',
  'interpret.life.transformation': 'A strong reflective pull toward change, renewal and reinvention.',

  /* Core dimensions (experimental) */
  'interpret.core.autonomy': 'A composite lean toward self-direction and independence.',
  'interpret.core.analytical_orientation': 'A composite lean toward analysis and reasoning.',
  'interpret.core.emotional_sensitivity': 'A composite lean toward emotional attunement.',
  'interpret.core.social_energy': 'A composite lean toward outward social energy.',
  'interpret.core.exploration': 'A composite lean toward exploring the new.',
  'interpret.core.structure': 'A composite lean toward structure and order.',
  'interpret.core.risk_tolerance': 'A composite lean toward tolerating risk.',
  'interpret.core.harmony': 'A composite lean toward harmony and accord.',
  'interpret.core.assertiveness': 'A composite lean toward assertiveness.',
  'interpret.core.meaning_orientation': 'A composite lean toward meaning and purpose.',
  'interpret.core.stability': 'A composite lean toward steadiness and stability.',
  'interpret.core.adaptability': 'A composite lean toward adapting to change.',
  'interpret.core.novelty_seeking': 'A composite lean toward seeking novelty.',
  'interpret.core.cooperation': 'A composite lean toward cooperation.',
  'interpret.core.dominance': 'A composite lean toward taking the lead.',
  'interpret.core.introspection': 'A composite lean toward introspection.',

  /* Cross-framework tensions (always questions) */
  'interpret.tension.introvert_service': 'You seem to recharge in solitude, yet feel a strong pull to serve others. How do you protect your energy while still giving what matters to you?',
  'interpret.tension.thinking_feeling_type': 'You tend to decide by logic, yet your deeper pattern is attuned to people. When do you let each guide you — and do they ever quietly disagree?',
  'interpret.tension.independent_trusting': 'You value standing on your own, yet you extend trust readily. Where does self-reliance end and openness begin for you?',
  'interpret.tension.planned_change': 'You like things settled, yet you are drawn to reinvention. What would it mean to plan for change rather than against it?',
  'interpret.tension.assert_harmony': 'You can take charge, yet you seek harmony. When you lead, how do you keep the peace without going quiet?',

  /* --- One-pager --- */
  'onepager.back': 'Back to SoulGraph',
  'onepager.download': 'Download PDF',
  'onepager.name_placeholder': 'Name or pseudonym',
  'onepager.untitled': 'My SoulGraph',
  'onepager.version': 'Version',
  'onepager.insights': 'Four things to notice',
  'onepager.enneagram_unclear': 'Pattern not yet clear',
  'onepager.caveat': 'A reflective map, not a diagnosis. Values are scaled to the possible range of the items you answered, not normed against a population. SoulGraph is not psychotherapy and makes no clinical, hiring or trustworthiness claims.',
  'onepager.referral_slot': '',

  /* --- Share card --- */
  'page.share.title': 'Share your constellation',
  'page.share.body': 'Export your SoulGraph figure as an image. You choose exactly what it shows — nothing is shared unless you say so.',
  'share.preview_alt': 'Preview of your SoulGraph share image',
  'share.include.title': 'What to include',
  'share.include.note': 'The figure alone reveals no scores. Codes are optional and off by default.',
  'share.include.figure': 'Figure only',
  'share.include.enneagram': 'Figure + Enneagram',
  'share.include.code': 'Figure + Jungian code',
  'share.action.share': 'Share',
  'share.action.download': 'Download PNG',
  'share.action.copy': 'Copy link',
  'share.downloaded': 'Image downloaded',
  'share.downloaded_desc': 'Sharing is not available here, so we saved the image instead.',
  'share.error': 'Something went wrong. Please try again.',
  'share.privacy_note': 'Images are generated entirely on your device. Nothing is uploaded, and your SoulGraph scores are never embedded in the figure-only card.',

  /* --- Method additions --- */
  'method.evidence.intro': 'Every value, insight and figure in SoulGraph carries exactly one of three labels, so you always know how firmly to read it.',
  'method.frameworks.title': 'The frameworks we use',
  'method.fw.jung.what': 'Four preference dimensions from Jungian tradition — where you draw energy, what you notice, how you decide, and how you order your days.',
  'method.fw.jung.standing': 'Interpretive. Intuitive and widely used; type theory has limited predictive validity in peer-reviewed research, so we show continuous positions, never a fixed box.',
  'method.fw.enneagram.what': 'Nine recurring motivational patterns, each with a neighbouring wing. We decline to name a type when your top two are too close.',
  'method.fw.enneagram.standing': 'Interpretive. A rich reflective tradition; empirical support for the nine-type structure is modest and debated.',
  'method.fw.life.what': 'Reflective life-theme questions, optionally blended with a numerological reading of your birth date.',
  'method.fw.life.standing': 'Experimental. Numerology has no empirical validation — offered only as a prompt for reflection.',
  'method.fw.core.what': 'Proprietary composites that combine signals across your other facets into higher-level dimensions.',
  'method.fw.core.standing': 'Experimental. Only as grounded as its inputs; the combination itself is unvalidated.',
  'method.scaled.title': 'Scaled, not normed',
  'method.scaled.body': 'A value of 70 means you scored 70% of the way up the possible range for the items you answered — not that you are "higher than 70% of people". We have no norm sample, and we will never dress a scaled score up as a percentile.',
  'method.confidence.title': 'How confidence is computed',
  'method.confidence.body': 'Each scale carries a confidence from three signals: coverage (how many of its items you answered), consistency (how well those answers agree), and penalties for straight-lining or implausibly fast responses. More items and more agreement raise confidence; it is never a measure of how "true" a result is.',

  /* --- Checkout / payment --- */
  'page.unlock.title': 'Unlock your SoulGraph',
  'page.unlock.body': 'The Complete SoulGraph unlocks the full assessment, your complete results, the one-pager and the share card — scored and stored entirely on your device. One payment, over Lightning. Entitlements are verifiable facts, not switches, and every price is read from configuration.',
  'assess.checking': 'Checking your entitlement…',
  'checkout.signin': 'Create or sign in to a Nostr identity to unlock the Complete SoulGraph.',
  'checkout.unlocked_title': 'Complete SoulGraph unlocked',
  'checkout.unlocked_body': 'Your payment is verified. This unlocks on this and any device you restore your key to.',
  'checkout.continue': 'Continue',
  'checkout.backup_title': 'Back up your key first',
  'checkout.backup_body': 'Your key is your SoulGraph and your proof of purchase. Back it up before you pay — if you lose it, nobody can recover it, and no payment can be re-linked to a key you no longer hold.',
  'checkout.receipt_warning_title': 'Zap receipts are public',
  'checkout.receipt_warning_body': 'Your payment is a Lightning zap, and its receipt is published to public relays. The receipt links your (pseudonymous) key to a SoulGraph purchase — never to any of your answers, scores or psychological data, which never leave your device unencrypted.',
  'checkout.pay': 'Pay with Lightning',
  'checkout.preparing': 'Preparing payment…',
  'checkout.scan': 'Scan with any Lightning wallet, or copy the invoice.',
  'checkout.copy_invoice': 'Copy invoice',
  'checkout.copied': 'Invoice copied',
  'checkout.check_again': 'Check again',
  'checkout.retry': 'Try again',
  'checkout.failed_title': 'Payment could not start',
  'checkout.support': 'Paid but not unlocked? Give the relays a moment and press “Check again”. Still stuck?',
  'checkout.support_link': 'Open the Privacy Center',
  'checkout.rate_loading': 'Fetching live rate…',
  'checkout.rate_unavailable': 'Live rate unavailable',
  'checkout.rate_asof': 'rate as of',
  'checkout.rate_cached': 'cached rate from',
  'checkout.state.verified_title': 'Payment verified',
  'checkout.state.verified_body': 'A valid zap receipt was found and verified. You are unlocked.',
  'checkout.state.detected_title': 'Payment detected — verifying',
  'checkout.state.detected_body': 'We found your receipt and are confirming it against the relays. This takes a few seconds.',
  'checkout.state.failed_title': 'Could not verify yet',
  'checkout.state.failed_body': 'We could not confirm a receipt. If you have paid, wait a moment and check again.',

  /* --- Unlock ladder --- */
  'unlock.ladder.title': 'The full pricing ladder',
  'unlock.ladder.note': 'Every price here is configuration, never hard-coded. Only Phase I products are purchasable today; the rest arrive with later phases.',
  'unlock.available_now': 'Available now',
  'unlock.coming_phase': 'Coming in a later phase',

  /* --- Invite / referral --- */
  'invite.title': 'Invite someone',
  'invite.body': 'Share SoulGraph with your link. First-touch attribution travels with every invitation and every payment.',
  'invite.link_label': 'Your referral link',
  'invite.copy': 'Copy link',
  'invite.copy_failed': 'Copy failed — copy the link manually.',
  'invite.code_label': 'Your code:',
  'invite.rewards_soon': 'Referral rewards launch soon — your invitations are already being counted.',
  'invite.attributed_prefix': 'Attributed sign-ups:',

  'footer.vibed': 'Vibed with Shakespeare',

  'theme.toggle': 'Toggle theme',
  'lang.toggle': 'Language',
};

// German — Swiss conventions ("ss", never "ß").
const de: Dict = {
  'app.name': 'SoulGraph',
  'app.tagline': 'Plattform für menschliche Intelligenz',

  'nav.graph': 'Mein SoulGraph',
  'nav.privacy': 'Privatsphäre',
  'nav.method': 'Methode',
  'nav.settings': 'Einstellungen',
  'nav.unlock': 'Freischalten',
  'nav.about': 'Über',
  'nav.begin': 'Mein SoulGraph erstellen',

  'landing.headline': 'Erkenne dich selbst. Verstehe andere.',
  'landing.subline': 'Eine private Karte davon, wie du denkst, entscheidest, dich verbindest und handelst.',
  'landing.cta': 'Mein SoulGraph erstellen',
  'landing.cta_note': 'Kein Konto erforderlich',
  'landing.law': 'Ein SoulGraph. Viele Anwendungen. Von dir kontrolliert. KI-gestützt. Menschlich verbunden.',

  'landing.how.title': 'So funktioniert es',
  'landing.how.assess.title': 'Erfassen',
  'landing.how.assess.body': 'Beantworte validierte Instrumente in deinem Tempo. Keine richtigen Antworten — nur deine.',
  'landing.how.map.title': 'Abbilden',
  'landing.how.map.body': 'Deine Antworten werden auf deinem Gerät zu einer strukturierten, versionierten Karte von dir ausgewertet.',
  'landing.how.understand.title': 'Verstehen',
  'landing.how.understand.body': 'Verwandle die Karte in klare Einsichten darüber, wie du denkst, entscheidest, dich verbindest und handelst.',

  'landing.privacy.title': 'Dein SoulGraph gehört dir',
  'landing.privacy.body': 'Deine Antworten werden auf deinem Gerät ausgewertet, verschlüsselt, bevor irgendetwas es verlässt, und niemals veröffentlicht. Die Relay-Synchronisation ist standardmässig ausgeschaltet. Es gibt keinen Server, der deinen SoulGraph lesen kann — weil es keinen Server gibt.',

  'landing.evidence.note': 'Jede Einsicht trägt ein Evidenz-Label — Psychometrisch, Interpretativ oder Experimentell (SoulGraph). SoulGraph diagnostiziert nie, urteilt nie und ist keine Psychotherapie.',

  'evidence.psychometric': 'Psychometrisch',
  'evidence.interpretive': 'Interpretativ',
  'evidence.experimental': 'Experimentell (SoulGraph)',

  'method.evidence.title': 'Drei Evidenz-Labels',
  'method.evidence.psychometric.body': 'Basiert auf Konstrukten mit substanzieller empirischer Grundlage — zum Beispiel den Big Five (OCEAN).',
  'method.evidence.interpretive.body': 'Aus etablierten interpretativen Traditionen — zum Beispiel der jungianischen Typologie oder dem Enneagramm.',
  'method.evidence.experimental.body': 'Proprietäre oder spekulative Composites — Life Pattern, Human Design und SoulGraph Core.',
  'method.boundaries.title': 'Was SoulGraph niemals tut',
  'method.boundaries.body': 'SoulGraph diagnostiziert niemals psychische Erkrankungen, psychiatrische oder medizinische Zustände, Kriminalität oder Gefährlichkeit. Es fällt niemals Urteile über Einstellung, Entlassung, Vertrauenswürdigkeit oder Stabilität und stellt probabilistische Interpretationen nie als Gewissheit dar. SoulGraph ist keine Psychotherapie. Organisationen besitzen niemals die SoulGraphs ihrer Mitarbeitenden — das wird kryptografisch durchgesetzt, nicht durch Nutzungsbedingungen.',

  'page.begin.title': 'Erstelle deinen SoulGraph',
  'page.begin.body': 'Eine kurze Demo mit 15 Fragen zum Einstieg. Deine Antworten werden in deinen vollständigen SoulGraph übernommen — nichts wird erneut gefragt.',
  'page.graph.title': 'Mein SoulGraph',
  'page.graph.body': 'Deine private, versionierte Karte. Facetten erscheinen hier, sobald du Erhebungen abschliesst.',
  'page.onepager.title': 'SoulGraph auf einer Seite',
  'page.onepager.body': 'Eine einseitige Zusammenfassung deines SoulGraph — für dich selbst oder zum einvernehmlichen Teilen.',
  'page.unlock.title': 'Schalte deinen SoulGraph frei',
  'page.unlock.body': 'Der Complete SoulGraph schaltet die vollständige Erfassung, deine kompletten Ergebnisse, den One-Pager und die Share-Karte frei — ausgewertet und gespeichert ausschliesslich auf deinem Gerät. Eine Zahlung, über Lightning. Berechtigungen sind überprüfbare Tatsachen, keine Schalter, und jeder Preis wird aus der Konfiguration gelesen.',
  'page.privacy_center.title': 'Privatsphäre-Zentrum',
  'page.privacy_center.body': 'Steuere Synchronisation, Freigaben und Widerruf. Nichts Psychologisches verlässt dein Gerät jemals unverschlüsselt.',
  'page.settings.title': 'Einstellungen',
  'page.settings.body': 'Identität, Erscheinungsbild, Sprache und Sync-Relays.',
  'page.method.title': 'Methode & Evidenz',
  'page.method.body': 'Wie SoulGraph auswertet, was jedes Evidenz-Label bedeutet und was SoulGraph niemals tun wird.',
  'page.legal.privacy.title': 'Datenschutz',
  'page.legal.terms.title': 'Nutzungsbedingungen',

  'placeholder.coming': 'Kartiert — noch nicht gebaut',

  /* --- Assessment --- */
  'assessment.progress.item': 'Frage',
  'assessment.likert.aria': 'Wie sehr stimmst du dieser Aussage zu?',
  'assessment.likert.disagree': 'Stimme nicht zu',
  'assessment.likert.agree': 'Stimme zu',
  'assessment.nav.back': 'Zurück',
  'assessment.nav.next': 'Weiter',
  'assessment.nav.finish': 'Meinen SoulGraph ansehen',
  'assessment.nav.scoring': 'Wird auf deinem Gerät ausgewertet…',
  'assessment.footer.local': 'Auf deinem Gerät ausgewertet. Verschlüsselt, bevor etwas gespeichert wird. Niemals veröffentlicht.',
  'assessment.footer.no_encrypt': 'Dein aktueller Signierer kann nicht verschlüsseln. Nutze eine NIP-07-Erweiterung oder einen lokalen Schlüssel mit NIP-44-Unterstützung, um deinen SoulGraph zu speichern.',
  'assessment.done.title': 'Dein SoulGraph ist kartiert',
  'assessment.done.body': 'Deine Antworten wurden auf deinem Gerät ausgewertet und in eine verschlüsselte, versionierte Karte versiegelt. Erkunde sie jetzt.',
  'assessment.done.cta': 'Meinen SoulGraph öffnen',

  /* --- Graph --- */
  'graph.summary.title': 'Gesamtvertrauen',
  'graph.summary.scaled': 'Skaliert, nicht an einer Bevölkerung normiert — ein Wert zeigt, wie weit oben im möglichen Bereich du liegst, nie ein Perzentil.',
  'graph.summary.full_cta': 'Tiefer gehen',
  'graph.footer.boundaries': 'SoulGraph diagnostiziert nie, urteilt nie und stellt Interpretation nie als Gewissheit dar. Es ist keine Psychotherapie. Dies ist eine reflektierende Karte — eine Tendenz, keine Schublade.',
  'graph.signin.body': 'Dein SoulGraph ist privat. Erstelle eine Nostr-Identität oder melde dich an, um zu beginnen.',
  'graph.empty.body': 'Du hast noch keinen SoulGraph kartiert. Die kostenlose Demo mit 15 Fragen dauert nur ein paar Minuten.',
  'graph.empty.cta': 'Demo starten',
  'graph.jung.code_note': 'ein praktisches Etikett, keine Schublade',
  'graph.life.code_note': 'reflektierende Musterzahl',
  'graph.enneagram.unclear': 'Deine zwei stärksten Muster liegen noch zu nah beieinander. Beantworte die vollständige Erhebung zur Klärung — wir wählen keines für dich aus.',
  'graph.enneagram.wing': 'mit Tendenz zu',
  'graph.core.title': 'SoulGraph Core',
  'graph.core.note': 'Experimentelle Composites über deine anderen Facetten. Ein Composite ist nur so fundiert wie seine Eingaben, und die Kombination selbst ist proprietär — stets experimentell.',
  'graph.identity.title': 'Identität',
  'graph.identity.note': 'Nur dein öffentlicher Schlüssel. Niemals dein bürgerlicher Name, deine Telefonnummer oder Adresse.',

  'jung.pole.energy.low': 'Nach innen',
  'jung.pole.energy.high': 'Nach aussen',
  'jung.pole.perception.low': 'Muster',
  'jung.pole.perception.high': 'Detail',
  'jung.pole.judgment.low': 'Werte',
  'jung.pole.judgment.high': 'Logik',
  'jung.pole.lifestyle.low': 'Offen',
  'jung.pole.lifestyle.high': 'Geplant',

  'core.dim.autonomy': 'Autonomie',
  'core.dim.analytical_orientation': 'Analytische Ausrichtung',
  'core.dim.emotional_sensitivity': 'Emotionale Sensibilität',
  'core.dim.social_energy': 'Soziale Energie',
  'core.dim.exploration': 'Erkundung',
  'core.dim.structure': 'Struktur',
  'core.dim.risk_tolerance': 'Risikobereitschaft',
  'core.dim.harmony': 'Harmonie',
  'core.dim.assertiveness': 'Durchsetzungsvermögen',
  'core.dim.meaning_orientation': 'Sinnorientierung',
  'core.dim.stability': 'Stabilität',
  'core.dim.adaptability': 'Anpassungsfähigkeit',
  'core.dim.novelty_seeking': 'Neugier auf Neues',
  'core.dim.cooperation': 'Kooperation',
  'core.dim.dominance': 'Dominanz',
  'core.dim.introspection': 'Introspektion',

  /* --- Vollständige Erhebung --- */
  'page.assess.kicker': 'Vollständiger SoulGraph',
  'page.assess.title': 'Vervollständige deinen SoulGraph',
  'page.assess.body': 'Die vollständige Erhebung über alle Instrumente. Du machst genau dort weiter, wo die Demo aufgehört hat — keine Frage wird je wiederholt. Pausiere jederzeit; dein Fortschritt wird verschlüsselt auf deinem Gerät gespeichert.',
  'assess.signin.body': 'Erstelle eine Nostr-Identität oder melde dich an, um die vollständige Erhebung zu machen.',
  'assess.locked.body': 'Der vollständige SoulGraph schaltet die ganze Erhebung und die vollständigen Ergebnisse frei.',
  'assess.locked.cta': 'Vollständigen SoulGraph freischalten',
  'assessment.resumed': 'Dort fortgesetzt, wo du aufgehört hast',
  'assessment.footer.resume': 'Du kannst jederzeit gehen — deine Antworten werden laufend gespeichert.',
  'assessment.allanswered.title': 'Alles ist beantwortet',
  'assessment.allanswered.body': 'Du hast bereits jede Frage über alle Instrumente beantwortet. Werte sie in eine neue Version deines SoulGraph aus.',

  'graph.action.onepager': 'Einseiter',
  'graph.action.share': 'Teilen',

  /* --- Vollständige Ergebnisse --- */
  'results.title': 'Vollständige Ergebnisse',
  'results.method_link': 'Wie wir das lesen →',
  'results.standing': 'Evidenz',
  'results.tensions.title': 'Widersprüche, die auffallen',
  'results.tensions.intro': 'Wo zwei Rahmen in unterschiedliche Richtungen ziehen. Das sind keine Probleme, die zu beheben wären — es sind Fragen, mit denen du verweilen kannst. Nur du kannst sie beantworten.',
  'interpret.tentative': 'Eine leichte Tendenz:',

  'interpret.jung.framework': 'Eine Linse aus der Jungschen Tradition, die habituelle Präferenzen über vier Dimensionen beschreibt. Sie bildet Tendenzen ab, keine festen Kategorien.',
  'interpret.jung.standing': 'Interpretativ. Die Dichotomien sind weit verbreitet und intuitiv nützlich, doch die Typentheorie hat in Fachstudien begrenzte Vorhersagekraft.',
  'interpret.enneagram.framework': 'Eine Karte von neun wiederkehrenden Beweggründen, jeder mit einem benachbarten „Flügel", der ihn färbt.',
  'interpret.enneagram.standing': 'Interpretativ. Reich als reflektierende Tradition; die empirische Stütze für die Neun-Typen-Struktur ist bescheiden und umstritten.',
  'interpret.life.framework': 'Eine experimentelle SoulGraph-Linse, die reflektierende Lebensthemen-Fragen mit einer optionalen numerologischen Lesart deines Geburtsdatums verbindet.',
  'interpret.life.standing': 'Experimentell. Numerologie hat keine empirische Validierung; verstehe dies rein als Anstoss zur Reflexion.',
  'interpret.core.framework': 'Proprietäre SoulGraph-Composites, die Signale über deine anderen Facetten zu übergeordneten Dimensionen verbinden.',
  'interpret.core.standing': 'Experimentell. Ein Composite ist nur so fundiert wie seine Eingaben, und die Kombination selbst ist unvalidiert.',

  'interpret.jung.energy.high': 'Du ziehst Energie eher aus Menschen und Aktivität, denkst laut und wendest dich nach aussen.',
  'interpret.jung.energy.low': 'Du lädst dich eher in Ruhe und Reflexion auf und ordnest deine Gedanken innerlich, bevor du sie teilst.',
  'interpret.jung.energy.balanced': 'Deine Energie bewegt sich fliessend zwischen Aussenwirkung und innerer Reflexion.',
  'interpret.jung.perception.high': 'Du neigst zu konkretem Detail und direkter Erfahrung statt zur Abstraktion.',
  'interpret.jung.perception.low': 'Du neigst zu Mustern, Möglichkeiten und dem Gesamtbild statt zum wörtlichen Detail.',
  'interpret.jung.perception.balanced': 'Du bewegst dich bequem zwischen konkretem Detail und breiteren Mustern.',
  'interpret.jung.judgment.high': 'Du wägst Entscheidungen eher zuerst nach Logik und Stimmigkeit ab.',
  'interpret.jung.judgment.low': 'Du wägst Entscheidungen eher zuerst nach Werten und ihrer Wirkung auf Menschen ab.',
  'interpret.jung.judgment.balanced': 'Du scheinst logische Stimmigkeit und menschliche Wirkung beim Entscheiden auszubalancieren.',
  'interpret.jung.lifestyle.high': 'Du bevorzugst Dinge eher geplant, festgelegt und abgeschlossen.',
  'interpret.jung.lifestyle.low': 'Du hältst Optionen lieber offen und passt dich unterwegs an.',
  'interpret.jung.lifestyle.balanced': 'Du hältst Struktur und Offenheit ungefähr im Gleichgewicht.',

  'interpret.enneagram.inconclusive': 'Deine zwei stärksten Muster liegen zu nah beieinander, um sie sicher zu trennen. Statt eines zu benennen, lassen wir das offen — die vollständige Erhebung kann es klären.',
  'interpret.enneagram.type.1': 'Ein Zug dazu, Dinge richtig zu machen und nach klaren Prinzipien zu leben.',
  'interpret.enneagram.type.2': 'Ein Zug dazu, die Bedürfnisse anderer zu spüren und zu erfüllen.',
  'interpret.enneagram.type.3': 'Ein Zug zu Zielen, Fortschritt und Wirksamkeit.',
  'interpret.enneagram.type.4': 'Ein Zug zu Tiefe, Echtheit und Selbstausdruck.',
  'interpret.enneagram.type.5': 'Ein Zug zu Verstehen, Rückzug und dem Bewahren von Energie.',
  'interpret.enneagram.type.6': 'Ein Zug zu Weitsicht, Loyalität und Sicherheit.',
  'interpret.enneagram.type.7': 'Ein Zug zu Möglichkeit, Vielfalt und offenen Optionen.',
  'interpret.enneagram.type.8': 'Ein Zug dazu, die Führung zu übernehmen und zu schützen, was zählt.',
  'interpret.enneagram.type.9': 'Ein Zug zu Ruhe, Einklang und innerem Halt.',
  'interpret.enneagram.wing.1.9': 'Dein Muster ist von einem ruhigeren, zurückgezogeneren Ton gefärbt.',
  'interpret.enneagram.wing.1.2': 'Dein Muster ist von Wärme und dem Wunsch zu helfen gefärbt.',
  'interpret.enneagram.wing.2.1': 'Dein Muster ist von Prinzip und Selbstdisziplin gefärbt.',
  'interpret.enneagram.wing.2.3': 'Dein Muster ist von Ehrgeiz und Ergebnisorientierung gefärbt.',
  'interpret.enneagram.wing.3.2': 'Dein Muster ist von Wärme und Aufmerksamkeit für andere gefärbt.',
  'interpret.enneagram.wing.3.4': 'Dein Muster ist von einer Suche nach Tiefe und Individualität gefärbt.',
  'interpret.enneagram.wing.4.3': 'Dein Muster ist von Antrieb und Erfolgsorientierung gefärbt.',
  'interpret.enneagram.wing.4.5': 'Dein Muster ist von Zurückhaltung und einem denkenden, beobachtenden Ton gefärbt.',
  'interpret.enneagram.wing.5.4': 'Dein Muster ist von Vorstellungskraft und emotionaler Tiefe gefärbt.',
  'interpret.enneagram.wing.5.6': 'Dein Muster ist von Vorsicht und dem Wunsch nach Sicherheit gefärbt.',
  'interpret.enneagram.wing.6.5': 'Dein Muster ist von einem zurückhaltenden, analytischen Ton gefärbt.',
  'interpret.enneagram.wing.6.7': 'Dein Muster ist von Optimismus und einem Sinn für Vielfalt gefärbt.',
  'interpret.enneagram.wing.7.6': 'Dein Muster ist von Loyalität und einem Blick aufs Risiko gefärbt.',
  'interpret.enneagram.wing.7.8': 'Dein Muster ist von Bestimmtheit und Antrieb gefärbt.',
  'interpret.enneagram.wing.8.7': 'Dein Muster ist von Begeisterung und einem Sinn fürs Neue gefärbt.',
  'interpret.enneagram.wing.8.9': 'Dein Muster ist von Beständigkeit und ruhigerer Art gefärbt.',
  'interpret.enneagram.wing.9.8': 'Dein Muster ist von stiller Stärke und Entschlossenheit gefärbt.',
  'interpret.enneagram.wing.9.1': 'Dein Muster ist von Prinzip und einem Sinn für Ordnung gefärbt.',

  'interpret.life.independence': 'Ein starker reflektierender Zug zu Selbstbestimmung und darauf, auf eigenen Füssen zu stehen.',
  'interpret.life.creativity': 'Ein starker reflektierender Zug zum Gestalten, Vorstellen und originellen Ausdruck.',
  'interpret.life.service': 'Ein starker reflektierender Zug dazu, zu anderen und zum Gemeinwohl beizutragen.',
  'interpret.life.discipline': 'Ein starker reflektierender Zug zu Struktur, Durchhalten und Meisterschaft.',
  'interpret.life.trust': 'Ein starker reflektierender Zug zu Offenheit und Vertrauen in andere.',
  'interpret.life.transformation': 'Ein starker reflektierender Zug zu Veränderung, Erneuerung und Neuerfindung.',

  'interpret.core.autonomy': 'Eine zusammengesetzte Tendenz zu Selbstbestimmung und Unabhängigkeit.',
  'interpret.core.analytical_orientation': 'Eine zusammengesetzte Tendenz zu Analyse und Denken.',
  'interpret.core.emotional_sensitivity': 'Eine zusammengesetzte Tendenz zu emotionaler Feinfühligkeit.',
  'interpret.core.social_energy': 'Eine zusammengesetzte Tendenz zu nach aussen gerichteter sozialer Energie.',
  'interpret.core.exploration': 'Eine zusammengesetzte Tendenz, Neues zu erkunden.',
  'interpret.core.structure': 'Eine zusammengesetzte Tendenz zu Struktur und Ordnung.',
  'interpret.core.risk_tolerance': 'Eine zusammengesetzte Tendenz, Risiko zu tolerieren.',
  'interpret.core.harmony': 'Eine zusammengesetzte Tendenz zu Harmonie und Einklang.',
  'interpret.core.assertiveness': 'Eine zusammengesetzte Tendenz zu Durchsetzungsvermögen.',
  'interpret.core.meaning_orientation': 'Eine zusammengesetzte Tendenz zu Sinn und Zweck.',
  'interpret.core.stability': 'Eine zusammengesetzte Tendenz zu Beständigkeit und Stabilität.',
  'interpret.core.adaptability': 'Eine zusammengesetzte Tendenz, sich an Wandel anzupassen.',
  'interpret.core.novelty_seeking': 'Eine zusammengesetzte Tendenz, Neues zu suchen.',
  'interpret.core.cooperation': 'Eine zusammengesetzte Tendenz zu Kooperation.',
  'interpret.core.dominance': 'Eine zusammengesetzte Tendenz, die Führung zu übernehmen.',
  'interpret.core.introspection': 'Eine zusammengesetzte Tendenz zu Introspektion.',

  'interpret.tension.introvert_service': 'Du scheinst dich in der Stille aufzuladen, spürst aber einen starken Zug, anderen zu dienen. Wie schützt du deine Energie und gibst zugleich, was dir wichtig ist?',
  'interpret.tension.thinking_feeling_type': 'Du entscheidest eher nach Logik, doch dein tieferes Muster ist auf Menschen eingestimmt. Wann lässt du jeweils das eine führen — und sind sie je leise uneins?',
  'interpret.tension.independent_trusting': 'Du schätzt es, auf eigenen Füssen zu stehen, schenkst aber bereitwillig Vertrauen. Wo endet für dich Selbstverlass und wo beginnt Offenheit?',
  'interpret.tension.planned_change': 'Du magst es festgelegt, fühlst dich aber zur Neuerfindung hingezogen. Was hiesse es, für den Wandel zu planen statt gegen ihn?',
  'interpret.tension.assert_harmony': 'Du kannst die Führung übernehmen, suchst aber Harmonie. Wenn du führst, wie wahrst du den Frieden, ohne zu verstummen?',

  /* --- Einseiter --- */
  'onepager.back': 'Zurück zum SoulGraph',
  'onepager.download': 'PDF herunterladen',
  'onepager.name_placeholder': 'Name oder Pseudonym',
  'onepager.untitled': 'Mein SoulGraph',
  'onepager.version': 'Version',
  'onepager.insights': 'Vier Dinge, die auffallen',
  'onepager.enneagram_unclear': 'Muster noch nicht klar',
  'onepager.caveat': 'Eine reflektierende Karte, keine Diagnose. Werte sind auf den möglichen Bereich der beantworteten Fragen skaliert, nicht an einer Bevölkerung normiert. SoulGraph ist keine Psychotherapie und erhebt keine klinischen, Einstellungs- oder Vertrauenswürdigkeits-Ansprüche.',
  'onepager.referral_slot': '',

  /* --- Teilen-Karte --- */
  'page.share.title': 'Teile deine Konstellation',
  'page.share.body': 'Exportiere deine SoulGraph-Figur als Bild. Du bestimmst genau, was sie zeigt — nichts wird geteilt, ausser du sagst es.',
  'share.preview_alt': 'Vorschau deines SoulGraph-Teilbildes',
  'share.include.title': 'Was aufgenommen wird',
  'share.include.note': 'Die Figur allein verrät keine Werte. Codes sind optional und standardmässig aus.',
  'share.include.figure': 'Nur die Figur',
  'share.include.enneagram': 'Figur + Enneagramm',
  'share.include.code': 'Figur + Jung-Code',
  'share.action.share': 'Teilen',
  'share.action.download': 'PNG herunterladen',
  'share.action.copy': 'Link kopieren',
  'share.downloaded': 'Bild heruntergeladen',
  'share.downloaded_desc': 'Teilen ist hier nicht verfügbar, daher haben wir das Bild stattdessen gespeichert.',
  'share.error': 'Etwas ist schiefgelaufen. Bitte versuche es erneut.',
  'share.privacy_note': 'Bilder werden vollständig auf deinem Gerät erzeugt. Nichts wird hochgeladen, und deine SoulGraph-Werte sind in der reinen Figur-Karte nie enthalten.',

  /* --- Methode (Ergänzungen) --- */
  'method.evidence.intro': 'Jeder Wert, jede Einsicht und jede Figur in SoulGraph trägt genau eines von drei Labels, damit du immer weisst, wie fest du sie lesen sollst.',
  'method.frameworks.title': 'Die Rahmen, die wir nutzen',
  'method.fw.jung.what': 'Vier Präferenz-Dimensionen aus der Jungschen Tradition — woher du Energie ziehst, was du bemerkst, wie du entscheidest und wie du deine Tage ordnest.',
  'method.fw.jung.standing': 'Interpretativ. Intuitiv und weit verbreitet; die Typentheorie hat in Fachstudien begrenzte Vorhersagekraft, daher zeigen wir kontinuierliche Positionen, nie eine feste Schublade.',
  'method.fw.enneagram.what': 'Neun wiederkehrende Beweggründe, jeder mit einem benachbarten Flügel. Wir benennen keinen Typ, wenn deine zwei stärksten zu nah beieinanderliegen.',
  'method.fw.enneagram.standing': 'Interpretativ. Eine reiche reflektierende Tradition; die empirische Stütze für die Neun-Typen-Struktur ist bescheiden und umstritten.',
  'method.fw.life.what': 'Reflektierende Lebensthemen-Fragen, optional verbunden mit einer numerologischen Lesart deines Geburtsdatums.',
  'method.fw.life.standing': 'Experimentell. Numerologie hat keine empirische Validierung — nur als Anstoss zur Reflexion angeboten.',
  'method.fw.core.what': 'Proprietäre Composites, die Signale über deine anderen Facetten zu übergeordneten Dimensionen verbinden.',
  'method.fw.core.standing': 'Experimentell. Nur so fundiert wie seine Eingaben; die Kombination selbst ist unvalidiert.',
  'method.scaled.title': 'Skaliert, nicht normiert',
  'method.scaled.body': 'Ein Wert von 70 bedeutet, dass du 70 % des möglichen Bereichs der beantworteten Fragen erreicht hast — nicht, dass du „höher als 70 % der Menschen" liegst. Wir haben keine Normstichprobe und verkleiden einen skalierten Wert nie als Perzentil.',
  'method.confidence.title': 'Wie Vertrauen berechnet wird',
  'method.confidence.body': 'Jede Skala trägt ein Vertrauen aus drei Signalen: Abdeckung (wie viele ihrer Fragen du beantwortet hast), Konsistenz (wie gut diese Antworten übereinstimmen) und Abzüge für gleichförmiges oder unplausibel schnelles Antworten. Mehr Fragen und mehr Übereinstimmung erhöhen das Vertrauen; es misst nie, wie „wahr" ein Ergebnis ist.',

  /* --- Checkout / Zahlung --- */
  'assess.checking': 'Deine Berechtigung wird geprüft…',
  'checkout.signin': 'Erstelle eine Nostr-Identität oder melde dich an, um den Complete SoulGraph freizuschalten.',
  'checkout.unlocked_title': 'Complete SoulGraph freigeschaltet',
  'checkout.unlocked_body': 'Deine Zahlung ist bestätigt. Das wird auf diesem und jedem Gerät freigeschaltet, auf dem du deinen Schlüssel wiederherstellst.',
  'checkout.continue': 'Weiter',
  'checkout.backup_title': 'Sichere zuerst deinen Schlüssel',
  'checkout.backup_body': 'Dein Schlüssel ist dein SoulGraph und dein Kaufnachweis. Sichere ihn, bevor du zahlst — geht er verloren, kann ihn niemand wiederherstellen, und keine Zahlung lässt sich mit einem Schlüssel verknüpfen, den du nicht mehr besitzt.',
  'checkout.receipt_warning_title': 'Zap-Quittungen sind öffentlich',
  'checkout.receipt_warning_body': 'Deine Zahlung ist ein Lightning-Zap, und die Quittung wird auf öffentlichen Relays veröffentlicht. Die Quittung verknüpft deinen (pseudonymen) Schlüssel mit einem SoulGraph-Kauf — niemals mit deinen Antworten, Werten oder psychologischen Daten, die dein Gerät nie unverschlüsselt verlassen.',
  'checkout.pay': 'Mit Lightning bezahlen',
  'checkout.preparing': 'Zahlung wird vorbereitet…',
  'checkout.scan': 'Mit einer beliebigen Lightning-Wallet scannen oder die Rechnung kopieren.',
  'checkout.copy_invoice': 'Rechnung kopieren',
  'checkout.copied': 'Rechnung kopiert',
  'checkout.check_again': 'Erneut prüfen',
  'checkout.retry': 'Erneut versuchen',
  'checkout.failed_title': 'Zahlung konnte nicht gestartet werden',
  'checkout.support': 'Bezahlt, aber nicht freigeschaltet? Gib den Relays einen Moment und drücke „Erneut prüfen". Immer noch blockiert?',
  'checkout.support_link': 'Privacy Center öffnen',
  'checkout.rate_loading': 'Aktueller Kurs wird geladen…',
  'checkout.rate_unavailable': 'Aktueller Kurs nicht verfügbar',
  'checkout.rate_asof': 'Kurs vom',
  'checkout.rate_cached': 'zwischengespeicherter Kurs vom',
  'checkout.state.verified_title': 'Zahlung bestätigt',
  'checkout.state.verified_body': 'Eine gültige Zap-Quittung wurde gefunden und geprüft. Du bist freigeschaltet.',
  'checkout.state.detected_title': 'Zahlung erkannt — wird geprüft',
  'checkout.state.detected_body': 'Wir haben deine Quittung gefunden und bestätigen sie über die Relays. Das dauert ein paar Sekunden.',
  'checkout.state.failed_title': 'Noch nicht bestätigt',
  'checkout.state.failed_body': 'Wir konnten keine Quittung bestätigen. Wenn du bezahlt hast, warte einen Moment und prüfe erneut.',

  /* --- Unlock-Leiter --- */
  'unlock.ladder.title': 'Die vollständige Preisleiter',
  'unlock.ladder.note': 'Jeder Preis hier ist Konfiguration, nie fest einprogrammiert. Nur Produkte der Phase I sind heute kaufbar; der Rest kommt mit späteren Phasen.',
  'unlock.available_now': 'Jetzt verfügbar',
  'unlock.coming_phase': 'Kommt in einer späteren Phase',

  /* --- Einladen / Empfehlung --- */
  'invite.title': 'Jemanden einladen',
  'invite.body': 'Teile SoulGraph mit deinem Link. First-Touch-Zuordnung reist mit jeder Einladung und jeder Zahlung mit.',
  'invite.link_label': 'Dein Empfehlungslink',
  'invite.copy': 'Link kopieren',
  'invite.copy_failed': 'Kopieren fehlgeschlagen — kopiere den Link manuell.',
  'invite.code_label': 'Dein Code:',
  'invite.rewards_soon': 'Empfehlungsprämien starten bald — deine Einladungen werden bereits gezählt.',
  'invite.attributed_prefix': 'Zugeordnete Anmeldungen:',

  'footer.vibed': 'Vibed mit Shakespeare',

  'theme.toggle': 'Erscheinungsbild wechseln',
  'lang.toggle': 'Sprache',
};

import { instrumentsEn, instrumentsDe } from './instruments';

export const TRANSLATIONS: Record<Locale, Dict> = {
  en: { ...en, ...instrumentsEn },
  de: { ...de, ...instrumentsDe },
};
